import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dashboardLink, navGroups, allNavItems, NavItem } from "../../seeds";
import { AuthContext } from "../../context/authContext";
import { SidebarContext } from "../../context/sidebarContext";
import { useGetFavouriteLinks, useToggleFavouriteLink } from "../../hooks/topNavApi/useFavourites";
import { useGetProfile } from "../../hooks/profileApi/getProfile";
import userGlobalConfig from "../../constants/globalConfig";
import { secureStorage } from "../../utils/secureStorage";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import NavSearch from "./navSearch";

// Which top-level menu is open — at most one at a time, name doubles as a
// discriminator ("favourites" | a navGroups[].key | null for none open).
type OpenMenu = string | null;

const TopNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setisAuthenticated } = useContext(AuthContext);
  // Repurposed from the old sidebar's open/close toggle — now drives the
  // mobile off-canvas menu instead. Header's hamburger button already calls
  // toggleSidebar(), so it needed no changes.
  const { isSidebarOpen: isMobileMenuOpen, toggleSidebar: toggleMobileMenu } = useContext(SidebarContext);
  const { data: profileData } = useGetProfile();
  const { data: favouritesData } = useGetFavouriteLinks();
  const { mutate: toggleFavourite } = useToggleFavouriteLink();
  const favourites = useMemo(() => favouritesData?.data?.favourites || [], [favouritesData]);

  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Close whatever dropdown is open on navigation, and on an outside click.
  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isGroupActive = (items: NavItem[]) => items.some((i) => isActive(i.path));

  const favouriteItems = useMemo(
    () => favourites.map((path) => allNavItems.find((i) => i.path === path)).filter((i): i is NavItem => !!i),
    [favourites]
  );

  const handleToggleFavourite = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavourite({ path });
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpenMenu(null);
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#72bcb8",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Log out!",
    }).then((result) => {
      if (result.isConfirmed) {
        secureStorage.removeItem(userGlobalConfig.TOKEN);
        secureStorage.removeItem(userGlobalConfig.USER_TOKEN);
        secureStorage.removeItem(userGlobalConfig.ADMIN_DETAILS);
        setisAuthenticated(false);
        toast.success("Logout Successfully");
      }
    });
  };

  return (
    <nav className="top-navbar" ref={navRef}>
      <div className="top-navbar-inner">
        <a
          href="#"
          className="top-navbar-brand"
          onClick={(e) => { e.preventDefault(); handleNavigate("/dashboard"); }}
        >
          <img src="assets/images/icons/logo.svg" alt="AirKeyHub" />
        </a>

        <NavSearch />

        <button
          className="top-navbar-mobile-toggle"
          type="button"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <i className={`fa-solid ${isMobileMenuOpen ? "fa-times" : "fa-bars"}`} />
        </button>

        <ul className={`top-navbar-menu ${isMobileMenuOpen ? "is-open" : ""}`}>
          <li className="top-navbar-item">
            <a
              href="#"
              className={`top-navbar-link ${isActive(dashboardLink.path) ? "active" : ""}`}
              onClick={(e) => { e.preventDefault(); handleNavigate(dashboardLink.path); }}
            >
              <i className="fa-solid fa-house me-1" />
              {dashboardLink.name}
            </a>
          </li>

          <li className={`top-navbar-item dropdown ${openMenu === "favourites" ? "is-open" : ""}`}>
            <a
              href="#"
              className="top-navbar-link"
              onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === "favourites" ? null : "favourites"); }}
            >
              <i className="fa-solid fa-star me-1" />
              Favourites
              {favouriteItems.length > 0 && <span className="top-navbar-badge">{favouriteItems.length}</span>}
            </a>
            <div className="top-navbar-dropdown">
              {favouriteItems.length === 0 ? (
                <div className="top-navbar-dropdown-empty">
                  No favourites yet — click the star next to any menu item below to pin it here.
                </div>
              ) : (
                favouriteItems.map((item) => (
                  <div
                    key={item.path}
                    role="button"
                    tabIndex={0}
                    className={`top-navbar-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                    onClick={() => handleNavigate(item.path)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleNavigate(item.path); }}
                  >
                    <span>{item.name}</span>
                    <button
                      className="top-navbar-star is-favourite"
                      title="Remove from favourites"
                      onClick={(e) => handleToggleFavourite(e, item.path)}
                    >
                      <i className="fa-solid fa-star" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </li>

          {navGroups.map((group) => (
            <li
              key={group.key}
              className={`top-navbar-item dropdown ${openMenu === group.key ? "is-open" : ""}`}
            >
              <a
                href="#"
                className={`top-navbar-link ${isGroupActive(group.items) ? "active" : ""}`}
                onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === group.key ? null : group.key); }}
              >
                <i className={`fa-solid ${group.icon} me-1`} />
                {group.label}
                <i className="fa-solid fa-chevron-down top-navbar-caret" />
              </a>
              <div className="top-navbar-dropdown">
                {group.items.map((item) => {
                  const favourited = favourites.includes(item.path);
                  return (
                    <div
                      key={item.path}
                      role="button"
                      tabIndex={0}
                      className={`top-navbar-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                      onClick={() => handleNavigate(item.path)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleNavigate(item.path); }}
                    >
                      <span>{item.name}</span>
                      <button
                        className={`top-navbar-star ${favourited ? "is-favourite" : ""}`}
                        title={favourited ? "Remove from favourites" : "Add to favourites"}
                        onClick={(e) => handleToggleFavourite(e, item.path)}
                      >
                        <i className={favourited ? "fa-solid fa-star" : "fa-regular fa-star"} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>

        <div className={`top-navbar-profile ${isMobileMenuOpen ? "is-open" : ""}`}>
          <div className={`dropdown top-navbar-item ${openMenu === "profile" ? "is-open" : ""}`}>
            <a
              href="#"
              className="top-navbar-link top-navbar-avatar"
              onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === "profile" ? null : "profile"); }}
            >
              <img
                src={profileData?.data?.image ?? "assets/images/icons/dummy-user.png"}
                alt="profile"
              />
            </a>
            <div className="top-navbar-dropdown top-navbar-dropdown-right">
              <a
                href="#"
                className="top-navbar-dropdown-item"
                onClick={(e) => { e.preventDefault(); handleNavigate("/profile"); }}
              >
                <span>Profile</span>
              </a>
              <a
                href="#"
                className="top-navbar-dropdown-item"
                onClick={(e) => { e.preventDefault(); handleLogout(); }}
              >
                <span>Logout</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
